import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/** @type {Map<string, { promise: Promise<string>; expiresAt: number }>} */
const prefetchedProductSections = new Map();
const PRODUCT_SECTION_PREFETCH_TTL = 30_000;

/** @type {{ id: string; url: string } | undefined} */
let pendingNewProductAfterMetaSwitch; // skipVariantBlock handoff when morph replaces the picker node

// A PDP morph can replace its picker, so request ownership must outlive one element instance.
let latestProductPageSelection = 0;

/** @type {AbortController | undefined} */
let activeProductPageRequest;

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /**
   * Preserved non-meta option values when switching products via meta object (e.g. 2pc→5pc).
   * Keyed by option name so 2pc (Color only) and 3pc (Serrated Knife + Color) can match correctly.
   * @type {Map<string, string>} optionName -> option value
   */
  #preservedNonMetaOptions = new Map();

  /**
   * When meta switch + restore skips the first VariantUpdateEvent, product-form still has the old product id.
   * The follow-up fetch's updateVariantPicker often returns no newProduct (picker already updated). Merge this in.
   * @type {{ id: string; url: string } | undefined}
   */
  #newProductAfterMetaSwitch = undefined;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /** @type {((event: Event) => void) | undefined} */
  #boundVariantChanged;

  /** @param {Event} event */
  #getOptionFromEvent(event) {
    if (!(event.target instanceof Element)) return null;
    if (event.target instanceof HTMLInputElement) return event.target;
    return event.target.closest('label')?.querySelector('input[data-connected-product-url]') ?? null;
  }

  /** @param {Event} event */
  #boundOptionPreview = (event) => {
    const option = this.#getOptionFromEvent(event);

    if (
      !(option instanceof HTMLInputElement) ||
      option.type !== 'radio' ||
      option.disabled ||
      option.dataset.optionAvailable === 'false'
    ) {
      return;
    }

    this.updateSelectedOption(option);
  };

  /** @param {Event} event */
  #boundOptionPrefetch = (event) => {
    if (this.dataset.templateProductMatch !== 'true') return;

    const option = this.#getOptionFromEvent(event);
    if (!(option instanceof HTMLInputElement)) return;

    const relatedTarget = event instanceof PointerEvent ? event.relatedTarget : null;
    if (
      event.type === 'pointerover' &&
      relatedTarget instanceof Node &&
      option.closest('label')?.contains(relatedTarget)
    ) {
      return;
    }

    this.#prefetchProductSection(option);
  };

  connectedCallback() {
    super.connectedCallback();
    this.#reinitializeRadiosAndCheckedIndices();
    const boundVariantChanged = this.#boundVariantChanged ?? this.variantChanged.bind(this);
    this.#boundVariantChanged = boundVariantChanged;
    this.addEventListener('change', boundVariantChanged);
    this.addEventListener('input', this.#boundOptionPreview);
    this.addEventListener('pointerover', this.#boundOptionPrefetch);
    this.#resizeObserver.observe(this);
  }

  #reinitializeRadiosAndCheckedIndices() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (
      Array.from(this.querySelectorAll('fieldset[data-fieldset-index]'))
    );
    this.#radios = [];
    this.#checkedIndices = [];
    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      let checkedIndex = -1;

      radios.forEach((radio, index) => {
        radio.dataset.previousChecked = 'false';
        if (radio.dataset.currentChecked === 'true' || radio.checked) {
          if (checkedIndex === -1) checkedIndex = index;
          radio.dataset.currentChecked = checkedIndex === index ? 'true' : 'false';
        } else {
          radio.dataset.currentChecked = 'false';
        }
      });

      this.#checkedIndices.push(checkedIndex !== -1 ? [checkedIndex] : []);

      fieldset.style.removeProperty('--pill-width-previous');
      if (checkedIndex === -1) {
        fieldset.style.removeProperty('--pill-width-current');
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#boundVariantChanged) {
      this.removeEventListener('change', this.#boundVariantChanged);
    }
    this.removeEventListener('input', this.#boundOptionPreview);
    this.removeEventListener('pointerover', this.#boundOptionPrefetch);
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const isInQuickAddModal = Boolean(event.target.closest('quick-add-dialog'));

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      (isOnProductPage || isInQuickAddModal) && !!newUrl && newUrl !== currentUrl;

    const isMetaSwitch = selectedOption.dataset.metaOption === 'true';
    const skipVariant = document.documentElement.dataset.variantPickerRestoreInProgress === '1';
    if (isMetaSwitch && loadsNewProduct) {
      this.#preservedNonMetaOptions = this.#getNonMetaOptionsByName(); // save product options before product switch
    } else {
      this.#preservedNonMetaOptions.clear();
    }
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector =
      loadsNewProduct && !isInQuickAddModal
        ? 'main'
        : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    // Keep PDP variant and connected-product responses scoped to the product section.
    // A full-page URL remains available as a fallback if the section response is incomplete.
    const productPageSectionId = isOnProductPage ? this.#productPageSectionId : null;
    const productPageSelection = isOnProductPage
      ? skipVariant
        ? latestProductPageSelection
        : ++latestProductPageSelection
      : undefined;
    const requestUrl = this.buildRequestUrl(selectedOption, null, [], productPageSectionId, {
      includeSelectedOptions: !loadsNewProduct,
    });
    const fallbackRequestUrl = productPageSectionId
      ? this.buildRequestUrl(selectedOption, null, [], null, { includeSelectedOptions: !loadsNewProduct })
      : undefined;
    this.fetchUpdatedSection(requestUrl, morphElementSelector, fallbackRequestUrl, {
      productPageSelection,
      coordinateProductPageRequest: isOnProductPage && !skipVariant,
    });

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (isMetaSwitch && loadsNewProduct) {
        url.searchParams.delete('variant');
      } else if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    const skipVariantFromMetaSwitch = isMetaSwitch && loadsNewProduct;
    if (!isInQuickAddModal) {
      if (!skipVariant && !skipVariantFromMetaSwitch && url.href !== window.location.href) {
        yieldToMainThread().then(() => {
          if (productPageSelection !== undefined && productPageSelection !== latestProductPageSelection) return;
          history.replaceState({}, '', url.toString());
        });
      } else if (skipVariantFromMetaSwitch && url.pathname !== window.location.pathname) {
        history.replaceState({}, '', url.toString());
      }
    }
  }

  get #productPageSectionId() {
    const section = this.closest('.shopify-section');
    if (!section?.id) return null;
    return section.id.replace(/^shopify-section-/, '');
  }

  /**
   * Prefetches the same product section used by a connected-product click.
   * @param {HTMLInputElement} option
   */
  #prefetchProductSection(option) {
    const connectedProductUrl = option.dataset.connectedProductUrl;
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const sectionId = this.#productPageSectionId;
    const connection = /** @type {{ connection?: { saveData?: boolean; effectiveType?: string } }} */ (
      /** @type {unknown} */ (navigator)
    ).connection;

    if (
      !connectedProductUrl ||
      !sectionId ||
      connectedProductUrl === currentUrl ||
      connection?.saveData ||
      connection?.effectiveType === 'slow-2g' ||
      connection?.effectiveType === '2g'
    ) {
      return;
    }

    const requestUrl = this.buildRequestUrl(option, null, [], sectionId, {
      includeSelectedOptions: false,
      updatePendingRequestUrl: false,
    });
    const cached = prefetchedProductSections.get(requestUrl);
    if (cached && cached.expiresAt > Date.now()) return;

    const promise = fetch(requestUrl)
      .then((response) => response.text())
      .catch((error) => {
        prefetchedProductSections.delete(requestUrl);
        throw error;
      });

    prefetchedProductSections.set(requestUrl, {
      promise,
      expiresAt: Date.now() + PRODUCT_SECTION_PREFETCH_TTL,
    });
    void promise.catch(() => {});
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;
    if (fieldset.classList.contains('variant-option--swatches')) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          for (const radio of radios) {
            radio.dataset.currentChecked = 'false';
            radio.dataset.previousChecked = 'false';
          }

          // Keep only the last two unique selections in order.
          const nextCheckedIndices = [inputIndex, ...checkedIndices.filter((index) => index !== inputIndex)].slice(0, 2);
          checkedIndices.length = 0;
          checkedIndices.push(...nextCheckedIndices);

          const [newCurrentIndex, newPreviousIndex] = checkedIndices;

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
          }

          requestAnimationFrame(() => {
            if (this.isConnected) this.updateFieldsetCss(fieldsetIndex);
          });
          this.#updateSimpleOptionValueLabel(fieldset, target.value);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Updates simple legend value text immediately on click.
   * Subgroup legends use the option-value custom element and sync themselves from picker events.
   * @param {HTMLFieldSetElement} fieldset
   * @param {string} value
   */
  #updateSimpleOptionValueLabel(fieldset, value) {
    if (fieldset.dataset.optionSubgroups === 'true') return;

    const label = fieldset.querySelector(':scope > legend .variant-option__swatch-value');
    if (label) label.textContent = value;
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @param {string | null} [sectionId] - Optional section ID for Section API requests.
   * @param {{ includeSelectedOptions?: boolean; updatePendingRequestUrl?: boolean }} [options] - Request-building options.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = [], sectionId = null, options = {}) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    if (options.updatePendingRequestUrl !== false) this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    const includeSelectedOptions = options.includeSelectedOptions !== false;
    if (includeSelectedOptions && this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (sectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?${[`section_id=${encodeURIComponent(sectionId)}`, ...params].join('&')}`;
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?${[`section_id=${SECTION_ID_MAP[closestSectionId]}`, ...params].join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  #clearSectionFetchRestoreState() {
    this.#newProductAfterMetaSwitch = undefined;
    pendingNewProductAfterMetaSwitch = undefined;
    delete this.dataset.skipUrlUpdateFromFetch;
    delete document.documentElement.dataset.skipUrlUpdateFromRestore;
    delete document.documentElement.dataset.variantPickerRestoreInProgress;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [fallbackRequestUrl] - A full-page fallback URL when section rendering does not return expected markup.
   * @param {{ productPageSelection?: number, coordinateProductPageRequest?: boolean }} [requestContext]
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, fallbackRequestUrl, requestContext = {}) {
    this.#abortController?.abort();
    if (requestContext.coordinateProductPageRequest) {
      activeProductPageRequest?.abort();
    }

    const abortController = new AbortController();
    this.#abortController = abortController;
    if (requestContext.coordinateProductPageRequest) {
      activeProductPageRequest = abortController;
    }

    const signal = abortController.signal;
    const isStale = () =>
      signal.aborted ||
      (requestContext.productPageSelection !== undefined &&
        requestContext.productPageSelection !== latestProductPageSelection);

    const cachedResponse = prefetchedProductSections.get(requestUrl);
    const responseTextPromise =
      cachedResponse && cachedResponse.expiresAt > Date.now()
        ? cachedResponse.promise
        : fetch(requestUrl, { signal }).then((response) => response.text());

    responseTextPromise.then(async (responseText) => {
        // Abort only rejects in-flight fetch/text(). If text() already resolved, this
        // callback still runs — bail so a stale non-main VariantUpdateEvent cannot
        // replaceWith the previous combined-listing child's media-gallery.
        if (isStale()) return;

        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          if (fallbackRequestUrl && fallbackRequestUrl !== requestUrl && !isStale()) {
            this.fetchUpdatedSection(fallbackRequestUrl, morphElementSelector, undefined, requestContext);
            return;
          }
          this.#clearSectionFetchRestoreState();
          return;
        }

        let newProduct;
        let newProductId;
        /** @type {boolean} */
        let restoreResult = false;

        if (morphElementSelector === 'main') {
          const skipRestore = document.documentElement.dataset.variantPickerRestoreInProgress === '1';
          const newPickerEl = html.querySelector('variant-picker');
          // Save current productId BEFORE updateMain changes it
          const currentProductId = this.dataset.productId;
          newProductId =
            (newPickerEl instanceof HTMLElement ? newPickerEl.dataset.productId : null) ?? this.dataset.productId;
          // Full `<main>` morph replaces the focused Size radio. Without restoring
          // scrollY the window jumps to the bottom (combined listings only; local
          // variant morph does not replace enough DOM to lose focus the same way).
          const scrollTop = document.scrollingElement?.scrollTop ?? window.scrollY;
          const variantJson = JSON.parse(textContent);
          this.updateMain(html);
          this.#morphBreadcrumbsFromFetchedDoc(html);
          this.#syncVariantPickerCheckedState(variantJson);

          const restoreScroll = () => {
            window.scrollTo({ top: scrollTop, left: 0, behavior: 'instant' });
          };
          restoreScroll();
          requestAnimationFrame(restoreScroll);
          const pickerForCss =
            (newProductId ? document.querySelector(`variant-picker[data-product-id="${newProductId}"]`) : null) ??
            document.querySelector('variant-picker');
          if (pickerForCss instanceof this.constructor) {
            const picker = /** @type {InstanceType<typeof VariantPicker>} */ (
              /** @type {unknown} */ (pickerForCss)
            );
            picker.#reinitializeRadiosAndCheckedIndices();
            picker.updateVariantPickerCss();
          }
          if (skipRestore) {
            delete document.documentElement.dataset.variantPickerRestoreInProgress;
          } else {
            const picker =
              (newProductId
                ? document.querySelector(`variant-picker[data-product-id="${newProductId}"]`)
                : null) ?? document.querySelector('variant-picker');
            if (picker instanceof HTMLElement) {
              restoreResult = await this.#restorePreservedOptionsAndTriggerChange(picker);
              if (isStale()) return;
              restoreScroll();
            }
          }

          if (
            newPickerEl instanceof HTMLElement &&
            newPickerEl.dataset.productId &&
            newPickerEl.dataset.productUrl &&
            currentProductId !== newPickerEl.dataset.productId
          ) {
            newProduct = { id: newPickerEl.dataset.productId, url: newPickerEl.dataset.productUrl };
            if (restoreResult) {
              this.#newProductAfterMetaSwitch = newProduct;
            }
          }

          // Rebuy.reinit() / layout may shift again on the next frame — pin scroll.
          // Also clear leftover mini-cart suppress from the previous product so ATC on a
          // non-upsell sibling still opens the drawer.
          if (document.body.hasAttribute('data-rebuy-pdp-ready') || window.Rebuy) {
            document.documentElement.dataset.variantPickerScrollTop = String(scrollTop);
            const cartDrawer = document.querySelector('cart-drawer-component');
            if (cartDrawer) cartDrawer.removeAttribute('rebuy-pdp-widget-complete');
          }
        } else if (morphElementSelector) {
          const currentProductId = this.dataset.productId;
          this.updateElement(html, morphElementSelector);
          const newPickerEl = html.querySelector('variant-picker');
          if (
            newPickerEl instanceof HTMLElement &&
            newPickerEl.dataset.productId &&
            newPickerEl.dataset.productUrl &&
            currentProductId !== newPickerEl.dataset.productId
          ) {
            newProduct = { id: newPickerEl.dataset.productId, url: newPickerEl.dataset.productUrl };
          }
        } else {
          newProduct = this.updateVariantPicker(html);
          this.#reinitializeRadiosAndCheckedIndices();
          this.updateVariantPickerCss();
          if (this.#preservedNonMetaOptions.size > 0) {
            restoreResult = await this.#restorePreservedOptionsAndTriggerChange(this);
            if (isStale()) return;
            if (restoreResult && newProduct) {
              this.#newProductAfterMetaSwitch = newProduct;
            }
          }
        }

        if (isStale()) return;

        if (document.body.hasAttribute('data-rebuy-pdp-ready') || window.Rebuy) {
          const buttons = document.querySelectorAll(
            '.product-information .buy-buttons-block add-to-cart-component button[name="add"]'
          );
          buttons.forEach((button) => {
            if (button.hasAttribute('rebuy-pdp-widget-ready')) return;
            button.setAttribute('rebuy-pdp-widget-ready', 'true');
            const suppressDrawer = () => {
              const cartDrawer = document.querySelector('cart-drawer-component');
              if (cartDrawer) cartDrawer.setAttribute('rebuy-pdp-widget-complete', 'false');
            };
            button.addEventListener('pointerdown', suppressDrawer);
            button.addEventListener('click', suppressDrawer);
          });

          // Wipe + reinit only after a connected-product PDP morph. Same-product variant
          // updates must not clear `rebuy.widgets` — that kills an in-flight Post-ATC popup
          // and any cart Rebuy mounts, and the matching `rebuy.init` would cancel the
          // no-popup mini-cart fallback mid-wait.
          if (morphElementSelector === 'main') {
            requestAnimationFrame(() => {
              if (isStale()) return;

              const rebuy =
                /** @type {{ widgets?: any[]; reinit?: () => void; init?: () => void } | undefined} */ (
                  /** @type {any} */ (window).Rebuy
                );
              // Form listeners stay on replaced ATC nodes. The section response keeps the
              // app block in the DOM with current product data so reinit can remount Post-ATC.
              // Do not destroy()/render() individual widgets; that can leave a dead Vue root.
              if (rebuy) {
                if (rebuy.widgets) rebuy.widgets.length = 0;
                if (typeof rebuy.reinit === 'function') {
                  rebuy.reinit();
                } else if (typeof rebuy.init === 'function') {
                  rebuy.init();
                }
              }
              document.dispatchEvent(new CustomEvent('rebuy.init', { bubbles: true }));

              const savedScroll = document.documentElement.dataset.variantPickerScrollTop;
              if (savedScroll != null) {
                const top = Number(savedScroll);
                window.scrollTo({ top, left: 0, behavior: 'instant' });
                requestAnimationFrame(() => window.scrollTo({ top, left: 0, behavior: 'instant' }));
                delete document.documentElement.dataset.variantPickerScrollTop;
              }
            });
          }
        }

        const skipVariantBlock = restoreResult === true;
        const pickerInDoc = document.querySelector('variant-picker');
        const variant = JSON.parse(textContent);
        const pickerForOptionId = morphElementSelector === 'main' ? pickerInDoc ?? this : this;
        const selectedOptionId =
          pickerForOptionId && 'selectedOptionId' in pickerForOptionId ? pickerForOptionId.selectedOptionId : undefined;
        const variantUpdateSourceId =
          selectedOptionId ?? (variant?.id != null ? String(variant.id) : undefined);

        const handoffNewProduct = newProduct ?? this.#newProductAfterMetaSwitch;
        if (skipVariantBlock && selectedOptionId && handoffNewProduct?.id)
          pendingNewProductAfterMetaSwitch = handoffNewProduct;

        if (!skipVariantBlock && variantUpdateSourceId) {
          const skipUrl =
            this.dataset.skipUrlUpdateFromFetch === '1' ||
            document.documentElement.dataset.skipUrlUpdateFromRestore === '1';
          if (
            morphElementSelector !== 'main' &&
            this.dataset.templateProductMatch === 'true' &&
            !this.closest('quick-add-dialog') &&
            variant?.id &&
            !skipUrl
          ) {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', String(variant.id));
            history.replaceState({}, '', url.toString());
          }
          delete this.dataset.skipUrlUpdateFromFetch;
          if (skipUrl) delete document.documentElement.dataset.skipUrlUpdateFromRestore;
          const productIdForEvent =
            morphElementSelector === 'main'
              ? ((pickerInDoc instanceof HTMLElement ? pickerInDoc.dataset.productId : null) ?? this.dataset.productId ?? '')
              : (this.dataset.productId ?? '');

          const mergedNewProduct = newProduct ?? this.#newProductAfterMetaSwitch;
          const event = new VariantUpdateEvent(variant, /** @type {string} */ (variantUpdateSourceId), {
            html,
            productId: productIdForEvent,
            newProduct: mergedNewProduct,
            morphedMain: morphElementSelector === 'main',
          });
          const productId = mergedNewProduct?.id ?? productIdForEvent ?? '';
          const dispatchTarget =
            morphElementSelector === 'main'
              ? (productId ? document.querySelector(`variant-picker[data-product-id="${productId}"]`) : null) ??
                document.querySelector('variant-picker')
              : this;
          dispatchTarget?.dispatchEvent(event);
          this.#newProductAfterMetaSwitch = undefined;
        }
      })
      .catch((error) => {
        if (isStale()) return;

        this.#clearSectionFetchRestoreState();
        console.error(error);
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const productInformationSelector = '[data-product-information-root], [data-testid="product-information"]';
    const newPartialProductInformation = newHtml.querySelector(
      '[data-product-information-root][data-variant-partial="true"], [data-testid="product-information"][data-variant-partial="true"]'
    );
    const currentProductInformation = document.querySelector(productInformationSelector);
    if (currentProductInformation instanceof HTMLElement && newPartialProductInformation instanceof HTMLElement) {
      morph(currentProductInformation, newPartialProductInformation, this.#productPageMorphOptions);
      return;
    }

    const rebuyNeedsFullMain =
      document.body.hasAttribute('data-rebuy-pdp-ready') ||
      Boolean(/** @type {{ Rebuy?: unknown }} */ (window).Rebuy);

    if (!rebuyNeedsFullMain) {
      const currentProductSection =
        this.closest('.shopify-section') ??
        document.querySelector(productInformationSelector)?.closest('.shopify-section');
      const newProductSection = currentProductSection?.id
        ? newHtml.getElementById(currentProductSection.id)
        : newHtml.querySelector(productInformationSelector)?.closest('.shopify-section');

      if (currentProductSection instanceof HTMLElement && newProductSection instanceof HTMLElement) {
        morph(currentProductSection, newProductSection, this.#productPageMorphOptions);
        return;
      }
    }

    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain, this.#productPageMorphOptions);
  }

  get #productPageMorphOptions() {
    return {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        if (node.classList.contains('product-media-container')) return undefined;
        return node.id || undefined;
      },
      onBeforeUpdate: (oldNode, newNode) => {
        if (
          oldNode instanceof Element &&
          newNode instanceof Element &&
          oldNode.closest('variant-picker') &&
          newNode.closest('variant-picker') &&
          (newNode instanceof HTMLInputElement || newNode instanceof HTMLOptionElement)
        ) {
          const isSelected =
            (newNode instanceof HTMLInputElement && newNode.checked) ||
            (newNode instanceof HTMLOptionElement && newNode.selected);
          newNode.setAttribute('data-current-checked', isSelected ? 'true' : 'false');
          newNode.setAttribute('data-previous-checked', 'false');
          return;
        }

        if (
          oldNode instanceof Element &&
          newNode instanceof Element &&
          oldNode.classList.contains('shopify-app-block') &&
          newNode.classList.contains('shopify-app-block') &&
          oldNode.querySelector('pdqproductpromise')
        ) {
          oldNode.setAttribute('data-skip-subtree-update', 'true');
          newNode.setAttribute('data-skip-subtree-update', 'true');

          // PDQ mounts React inside this app block. Preserve the runtime
          // min-height used to keep the product details layout stable while
          // PDQ replaces its contents during a combined-listing switch.
          const reservedMinHeight = oldNode.style.getPropertyValue('min-height');
          if (reservedMinHeight) {
            newNode.style.setProperty('min-height', reservedMinHeight);
          }
          return;
        }

        MORPH_OPTIONS.onBeforeUpdate?.(oldNode, newNode);
      },
    };
  }

  #morphBreadcrumbsFromFetchedDoc(/** @type {Document} */ fetchedDoc) {
    const live = document.querySelector('.breadcrumbs-section .breadcrumbs__list');
    const next = fetchedDoc.querySelector('.breadcrumbs-section .breadcrumbs__list');
    if (!(live instanceof HTMLElement) || !(next instanceof HTMLElement)) return;
    morph(live, next, MORPH_OPTIONS);
  }

  /**
   * Collects non-meta option values keyed by option name (skips meta option).
   * @returns {Map<string, string>} optionName -> option value
   */
  #getNonMetaOptionsByName() {
    const map = new Map();
    const optionContainers = this.querySelectorAll(
      'fieldset[data-fieldset-index][data-option-name], [data-fieldset-index][data-option-name]'
    );
    for (const container of optionContainers) {
      if (!(container instanceof HTMLElement)) continue;
      if (container.dataset.metaOption === 'true') continue;
      const optionName = container.dataset.optionName;
      if (!optionName) continue;
      const checked = container.querySelector('input:checked, option[selected]');
      if (checked instanceof HTMLInputElement && checked.value) {
        map.set(optionName, checked.value);
      } else if (checked instanceof HTMLOptionElement && checked.value) {
        map.set(optionName, checked.value);
      }
    }
    return map;
  }

  /**
   * Restores preserved options. Updates pill state and URL via product JSON (section response can be wrong for multi-option).
   * Does not dispatch change to avoid fetch race; caller must use returned variant for VariantUpdateEvent.
   * @param {HTMLElement} picker - The variant picker element (may be new after morph).
   * @returns {Promise<boolean>} True if options were restored and change was dispatched.
   */
  async #restorePreservedOptionsAndTriggerChange(picker) {
    if (this.#preservedNonMetaOptions.size === 0) return false;
    const optionContainers = picker.querySelectorAll(
      'fieldset[data-fieldset-index][data-option-name], [data-fieldset-index][data-option-name]'
    );
    /** @type {HTMLInputElement | HTMLSelectElement | null} */
    let lastRestored = null;
    for (const container of optionContainers) {
      if (!(container instanceof HTMLElement)) continue;
      if (container.dataset.metaOption === 'true') continue;
      const optionName = container.dataset.optionName;
      if (!optionName) continue;
      const savedValue = this.#preservedNonMetaOptions.get(optionName);
      if (!savedValue) continue;
      for (const input of container.querySelectorAll('input[type="radio"]')) {
        if (input instanceof HTMLInputElement) {
          input.removeAttribute('data-current-checked');
          input.removeAttribute('data-previous-checked');
        }
      }
      const input = container.querySelector(`input[value="${CSS.escape(savedValue)}"]`);
      const select = container.querySelector('select');
      if (input instanceof HTMLInputElement) {
        input.checked = true;
        input.setAttribute('data-current-checked', 'true');
        lastRestored = input;
      } else if (select && select instanceof HTMLSelectElement) {
        const option = Array.from(select.options).find((o) => o.value === savedValue);
        if (option) {
          for (const opt of select.options) opt.removeAttribute('selected');
          select.value = savedValue;
          option.selected = true;
          option.setAttribute('selected', 'selected');
          lastRestored = select;
        }
      } else {
        const checkedRadio = container.querySelector('input[type="radio"]:checked');
        if (checkedRadio instanceof HTMLInputElement) {
          checkedRadio.setAttribute('data-current-checked', 'true');
        }
      }
    }
    const savedByOptionName = new Map(this.#preservedNonMetaOptions);
    this.#preservedNonMetaOptions.clear();
    if (lastRestored) {
      picker.dataset.skipUrlUpdateFromFetch = '1';
      document.documentElement.dataset.variantPickerRestoreInProgress = '1';
      document.documentElement.dataset.skipUrlUpdateFromRestore = '1';
      lastRestored.dispatchEvent(new Event('change', { bubbles: true }));
      if (picker instanceof VariantPicker) {
        picker.updateVariantPickerCss();
      }
      const productUrl = picker.dataset.productUrl;
      if (productUrl) {
        await this.#updateUrlWithVariantFromProduct(productUrl, savedByOptionName, picker);
      }
      delete document.documentElement.dataset.variantPickerRestoreInProgress;
      return true;
    }
    const productUrl = picker.dataset.productUrl;
    if (productUrl) {
      await this.#updateUrlWithVariantFromProduct(productUrl, savedByOptionName, picker);
    }
    return false;
  }

  /**
   * Fetches product JSON and updates URL with variant matching option values.
   * Section response can return wrong variant for multi-option products.
   * @param {string} productUrl
   * @param {Map<string, string>} savedByOptionName
   * @param {HTMLElement} picker
   * @returns {Promise<object | null>} The matched variant or null.
   */
  async #updateUrlWithVariantFromProduct(productUrl, savedByOptionName, picker) {
    if (this.closest('quick-add-dialog')) return null;
    const jsonUrl = productUrl.replace(/\/$/, '') + '.js';
    try {
      const res = await fetch(jsonUrl, { signal: this.#abortController?.signal });
      const product = await res.json();
      const options = product.options || [];
      const optionNames = options.map(
        /** @param {string | { name?: string }} opt */
        (opt) => (typeof opt === 'string' ? opt : opt?.name ?? '')
      );
      const optionValidValues = new Map();
      for (let i = 0; i < options.length; i++) {
        const opt = options[i];
        const name = typeof opt === 'string' ? opt : opt?.name ?? '';
        const values = typeof opt === 'object' && Array.isArray(opt?.values) ? opt.values : [];
        if (name) optionValidValues.set(name, new Set(values));
      }
      const currentFromPicker = new Map();
      for (const container of picker.querySelectorAll(
        'fieldset[data-fieldset-index][data-option-name], [data-fieldset-index][data-option-name]'
      )) {
        if (!(container instanceof HTMLElement) || container.dataset.metaOption === 'true') continue;
        const optionName = container.dataset.optionName;
        if (!optionName) continue;
        const checked = container.querySelector('input:checked, option[selected]');
        if (checked instanceof HTMLInputElement && checked.value) {
          currentFromPicker.set(optionName, checked.value);
        } else if (checked instanceof HTMLOptionElement && checked.value) {
          currentFromPicker.set(optionName, checked.value);
        }
      }
      /** @param {string} name */
      const getOptionValue = (name) => {
        const savedValue = savedByOptionName.get(name);
        const currentValue = currentFromPicker.get(name) ?? '';
        if (savedValue === undefined) return currentValue;
        const validValues = optionValidValues.get(name);
        if (validValues && !validValues.has(savedValue)) return currentValue;
        return savedValue;
      };
      const optionValues = /** @type {string[]} */ (optionNames).map(getOptionValue);
      const variant = product.variants?.find(
        /** @param {{ option1?: string; option2?: string; option3?: string; id?: number }} v */
        (v) =>
          (v.option1 === optionValues[0] || !optionValues[0]) &&
          (v.option2 === optionValues[1] || !optionValues[1]) &&
          (v.option3 === optionValues[2] || !optionValues[2])
      );
      if (variant?.id) {
        const base = productUrl.startsWith('http') ? productUrl : `${window.location.origin}${productUrl.startsWith('/') ? '' : '/'}${productUrl}`;
        const url = new URL(base.replace(/\/$/, ''));
        url.searchParams.set('variant', String(variant.id));
        history.replaceState({}, '', url.toString());
        return variant;
      }
      return null;
    } catch (err) {
      if (err && err.name === 'AbortError') return null;
      console.warn('[variant-picker] Failed to update URL from product JSON:', err);
      return null;
    }
  }

  /** @param {{ id?: number; product_id?: string | number } | null} variant */
  #syncVariantPickerCheckedState(variant) {
    if (!variant?.id) return;
    const picker = document.querySelector(`variant-picker[data-product-id="${variant.product_id ?? this.dataset.productId}"]`) ?? document.querySelector('variant-picker');
    if (!picker) return;
    const variantId = String(variant.id);
    const targets = picker.querySelectorAll(`input[data-variant-id="${variantId}"], option[data-variant-id="${variantId}"]`);
    for (const target of targets) {
      if (target instanceof HTMLInputElement) {
        target.checked = true;
        target.setAttribute('data-current-checked', 'true');
      } else if (target instanceof HTMLOptionElement) {
        const select = target.closest('select');
        if (select) {
          for (const opt of select.options) opt.removeAttribute('selected');
          select.value = target.value;
        }
        target.selected = true;
        target.setAttribute('selected', 'selected');
      }
    }
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    );
    const nonMeta = selectedOptions.filter((opt) => {
      const container = opt.closest('[data-option-name]');
      return !(container instanceof HTMLElement) || container.dataset.metaOption !== 'true';
    });
    const selectedOption = nonMeta[0] ?? selectedOptions[0];
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }
    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId, variantId } = selectedOption.dataset;
    return optionValueId || variantId || undefined;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return /** @type {string[]} */ (
      selectedOptions
        .filter((option) => {
          const container = option.closest('[data-option-name]');
          return !(container instanceof HTMLElement) || container.dataset.metaOption !== 'true';
        })
        .map((option) => (option instanceof HTMLElement ? option.dataset.optionValueId : undefined))
        .filter(Boolean)
    );
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
